import { Fullscreen } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import pauseButton from "../../../assets/common/VideoIcons/pause-button.svg";
import playButton from "../../../assets/common/VideoIcons/play-button.svg";
import styles from "./BriaVideo.module.scss";

export interface BriaVideoProps {
	thumbnail?: string;
	videoSrc: string;
	autoPlay?: boolean;
	replay?: boolean;
	showTransparentGridBackground?: boolean;
	className?: string;
}

const BriaVideo: React.FC<BriaVideoProps> = ({
	thumbnail,
	videoSrc,
	autoPlay = false,
	replay = true,
	showTransparentGridBackground = false,
	className,
}) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const progressRef = useRef<HTMLDivElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [hasStarted, setHasStarted] = useState(false);
	const [showControls, setShowControls] = useState(false);
	const [showOverlays, setShowOverlays] = useState(true);
	const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragTime, setDragTime] = useState<number | null>(null);
	const thumbRef = useRef<HTMLDivElement>(null);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const pendingPlayRef = useRef<Promise<void> | null>(null);

	const clearHideTimeout = () => {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
		}
	};

	const startHideTimer = () => {
		clearHideTimeout();
		hideTimeoutRef.current = setTimeout(() => {
			setShowControls(false);
			setShowOverlays(false);
		}, 4000);
	};

	const handleToggleFullScreen = () => {
		const video = videoRef.current;
		if (!video) return;

		video.requestFullscreen();
	};

	const handleMouseEnter = () => {
		const video = videoRef.current;
		if (!video) return;

		if (video.paused && autoPlay) {
			const playPromise = video.play();
			if (playPromise && typeof playPromise.then === "function") {
				pendingPlayRef.current = playPromise
					.then(() => {
						setIsPlaying(true);
						setShowControls(true);
						setShowOverlays(true);
						startHideTimer();
						pendingPlayRef.current = null;
					})
					.catch((err) => {
						if (err && err.name !== "AbortError") {
							console.warn("Video play() failed on hover:", err);
						}
						pendingPlayRef.current = null;
					});
			}
		}
		if (isPlaying) {
			clearHideTimeout();
			setShowControls(true);
			setShowOverlays(true);
		}
	};

	const handleMouseLeave = () => {
		const video = videoRef.current;
		if (!video) return;

		const videoWrapper = video.closest('[class*="videoWrapper"]');
		const isInFullScreenViewer = videoWrapper?.closest('[class*="overlay"]') !== null;

		if (!video.paused && autoPlay && !isFullscreen && !isInFullScreenViewer) {
			video.pause();
			video.currentTime = 0;
			setIsPlaying(false);
			setShowControls(false);
			setCurrentTime(0);
			clearHideTimeout();
		} else if (isPlaying) {
			startHideTimer();
		}
	};

	const togglePlay = () => {
		const video = videoRef.current;
		if (!video || isFullscreen) return;

		if (video.paused) {
			const playPromise = video.play();
			if (playPromise && typeof playPromise.then === "function") {
				pendingPlayRef.current = playPromise.catch((err) => {
					if (err && err.name !== "AbortError") {
						console.warn("Video play() failed:", err);
					}
					pendingPlayRef.current = null;
				});
			}
			setIsPlaying(true);
			setShowControls(true);
			setShowOverlays(true);
			startHideTimer();
		} else {
			// If a play() is pending, let it resolve/reject silently
			pendingPlayRef.current = null;
			video.pause();
			setIsPlaying(false);
			setShowControls(false);
			clearHideTimeout();
		}
	};

	const handleProgressClick = (e: React.MouseEvent) => {
		const video = videoRef.current;
		const progressBar = progressRef.current;
		if (!video || !progressBar) return;

		const rect = progressBar.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		const width = rect.width;
		const newTime = (clickX / width) * duration;

		video.currentTime = newTime;
		setCurrentTime(newTime);
	};

	const formatTime = (time: number) => {
		if (!time || isNaN(time)) return "0:00";
		const mins = Math.floor(time / 60);
		const secs = Math.floor(time % 60)
			.toString()
			.padStart(2, "0");
		return `${mins}:${secs}`;
	};

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		if (autoPlay) {
			const playPromise = video.play();
			if (playPromise && typeof playPromise.then === "function") {
				pendingPlayRef.current = playPromise
					.then(() => {
						setIsPlaying(true);
						setShowControls(true);
						setShowOverlays(true);
						startHideTimer();
						pendingPlayRef.current = null;
					})
					.catch((err) => {
						if (err && err.name !== "AbortError") {
							console.warn("Video autoplay failed:", err);
						}
						pendingPlayRef.current = null;
					});
			} else {
				video.play();
				setIsPlaying(true);
				setShowControls(true);
				setShowOverlays(true);
				startHideTimer();
			}
		}

		const updateTime = () => {
			if (!isDragging) {
				setCurrentTime(video.currentTime);
			}
			setDuration(video.duration || 0);
			if (video.currentTime > 0) {
				setHasStarted(true);
			}
		};

		const handleFullscreenChange = () => {
			setIsFullscreen(videoRef.current?.contains(document.fullscreenElement) ?? false);
		};

		const handleFullScreenPlay = () => {
			if (isFullscreen) {
				setIsPlaying(true);
				setShowControls(true);
				setShowOverlays(true);
				startHideTimer();
			}
		};

		const handleFullScreenPause = () => {
			if (isFullscreen) {
				setIsPlaying(false);
				setShowControls(false);
				clearHideTimeout();
			}
		};

		video.addEventListener("play", handleFullScreenPlay);
		video.addEventListener("pause", handleFullScreenPause);
		document.addEventListener("fullscreenchange", handleFullscreenChange);
		video.addEventListener("timeupdate", updateTime);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
			video.removeEventListener("play", handleFullScreenPlay);
			video.removeEventListener("pause", handleFullScreenPause);
			video.removeEventListener("timeupdate", updateTime);
			clearHideTimeout();
		};
	}, []);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging || !progressRef.current || !videoRef.current) return;

			const rect = progressRef.current.getBoundingClientRect();
			let offsetX = e.clientX - rect.left;
			offsetX = Math.max(0, Math.min(offsetX, rect.width));
			const newTime = (offsetX / rect.width) * duration;

			setDragTime(newTime);
		};

		const handleMouseUp = () => {
			if (!isDragging || !videoRef.current || dragTime == null) return;
			videoRef.current.currentTime = dragTime;
			setCurrentTime(dragTime);
			setDragTime(null);
			setIsDragging(false);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [isDragging, dragTime, duration]);

	const progressPercent = duration > 0 ? ((dragTime ?? currentTime) / duration) * 100 : 0;

	return (
		<Box
			className={clsx(styles.videoWrapper, hasStarted ? styles.playing : styles.notStarted, className)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<video
				ref={videoRef}
				poster={thumbnail}
				className={clsx(styles.video, {
					[styles.transparentGridBackground]: showTransparentGridBackground,
				})}
				onClick={togglePlay}
				playsInline
				loop={replay}
				preload="metadata"
				src={videoSrc}
			/>

			{!isPlaying && (
				<Box component={"button"} className={styles.centerPlayButton} onClick={togglePlay}>
					<img src={playButton} alt="Play" />
				</Box>
			)}

			{(videoRef.current?.paused || showOverlays) && <Box className={styles.topOverlay} />}
			{(videoRef.current?.paused || showOverlays) && <Box className={styles.bottomOverlay} />}
			{showControls && (
				<Box className={styles.fullscreenButton} onClick={handleToggleFullScreen}>
					<Fullscreen />
				</Box>
			)}
			{showControls && (
				<Box className={styles.controls}>
					<Box className={styles.bottomRow}>
						<Box component={"button"} className={styles.playPause} onClick={togglePlay}>
							<img
								src={isPlaying ? pauseButton : playButton}
								alt={isPlaying ? "Pause" : "Play"}
								className={styles.icon}
							/>
						</Box>

						<Box
							className={styles.progressBar}
							ref={progressRef}
							onClick={(e) => !isDragging && handleProgressClick(e)}
						>
							<Box className={styles.progress} style={{ width: `${progressPercent}%` }} />
							<Box
								ref={thumbRef}
								className={styles.thumb}
								style={{ left: `${progressPercent}%` }}
								onMouseDown={() => setIsDragging(true)}
							>
								{isDragging && <Box className={styles.tooltip}>{formatTime(dragTime ?? 0)}</Box>}
							</Box>
						</Box>

						<Typography className={styles.time}>
							{formatTime(currentTime)} / {formatTime(duration)}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default observer(BriaVideo);
